import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Kokochao V15 product shell.
 * Horizon-inspired sticky purchase behavior adapted to the current Dawn-compatible product lifecycle.
 */
class KcH15ProductShell extends Component {
  #abortController = new AbortController();
  #buyObserver = null;
  #footerObserver = null;
  #mutationObserver = null;
  #hasPassedBuyButtons = false;
  #nearFooter = false;
  #variants = [];

  connectedCallback() {
    super.connectedCallback();

    this.stickyCart = this.querySelector('[data-kc-h15-sticky-cart]');
    this.productForm = this.querySelector(`#${CSS.escape(this.dataset.productFormId || '')}`);
    this.buyButtons = this.querySelector('[data-kc-h15-buy-buttons]');
    this.mainSubmit = this.querySelector('[id^="ProductSubmitButton-"]');
    this.idInput = this.productForm?.querySelector('input[name="id"]') || null;
    this.quantityInput = this.productForm?.querySelector('input[name="quantity"]') || null;

    const variantsScript = this.querySelector('[data-kc-h15-variants]');
    if (variantsScript) {
      try {
        this.#variants = JSON.parse(variantsScript.textContent || '[]');
      } catch (_) {
        this.#variants = [];
      }
    }

    if (this.dataset.productId) RecentlyViewed.addProduct(String(this.dataset.productId));
    this.#bindEvents();
    this.#setupObservers();
    this.#setupResponsiveDetails();
    requestAnimationFrame(() => this.#dedupeDescriptionFacts());
    this.#syncVariantState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#buyObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#mutationObserver?.disconnect();
  }

  #dedupeDescriptionFacts() {
    const copy = document.querySelector('.kc-pdp-v4-copy');
    if (!copy) return;

    const presentKeys = new Set(
      [...document.querySelectorAll('[data-kc-pim-key]')]
        .map((node) => node.dataset.kcPimKey)
        .filter(Boolean)
    );
    if (!presentKeys.size) return;

    const normalize = (value) =>
      String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();

    const keyByLabel = new Map([
      ['marque', 'brand'],
      ['fabricant', 'manufacturer'],
      ['gamme', 'range'],
      ['licence', 'license'],
      ['license', 'license'],
      ['serie', 'series'],
      ['personnage', 'character'],
      ['univers', 'universe'],
      ['animal', 'animal'],
      ['hauteur', 'height'],
      ['dimensions', 'dimensions'],
      ['dimension', 'dimensions'],
      ['matiere', 'material'],
      ['matiere / composition', 'material'],
      ['rembourrage', 'filling'],
      ['remplissage', 'filling'],
      ['finition', 'finish'],
      ['format', 'format'],
      ['nombre de pieces', 'pieces'],
      ['espace d’usage', 'usage'],
      ["espace d'usage", 'usage'],
    ]);

    copy.querySelectorAll('li, p').forEach((node) => {
      const text = normalize(node.textContent);
      const separator = text.indexOf(':');
      if (separator < 1 || separator > 34) return;
      const label = text.slice(0, separator).trim();
      const key = keyByLabel.get(label);
      if (key && presentKeys.has(key)) node.hidden = true;
    });

    copy.querySelectorAll('ul, ol').forEach((list) => {
      const visibleItems = [...list.querySelectorAll(':scope > li')].filter((item) => !item.hidden);
      if (visibleItems.length) return;
      list.hidden = true;
      const heading = list.previousElementSibling;
      if (heading && /^caracteristiques?\b/.test(normalize(heading.textContent))) heading.hidden = true;
    });
  }

  #setupResponsiveDetails() {
    const details = [...this.querySelectorAll('[data-kc-mobile-collapse]')];
    if (!details.length) return;

    const media = window.matchMedia('(max-width: 749px)');
    const sync = () => {
      details.forEach((detail) => {
        if (media.matches) {
          if (detail.dataset.kcMobileReady !== 'true') {
            detail.open = false;
            detail.dataset.kcMobileReady = 'true';
          }
        } else {
          detail.open = true;
          delete detail.dataset.kcMobileReady;
        }
      });
    };

    sync();
    media.addEventListener('change', sync, { signal: this.#abortController.signal });
  }

  #bindEvents() {
    const { signal } = this.#abortController;

    this.productForm?.addEventListener('change', () => queueMicrotask(() => this.#syncVariantState()), { signal });
    this.productForm?.addEventListener('input', () => queueMicrotask(() => this.#syncQuantityState()), { signal });
    document.addEventListener('variant-change', () => queueMicrotask(() => this.#syncVariantState()), { signal });
    document.addEventListener('option-value-selection-change', () => setTimeout(() => this.#syncVariantState(), 80), { signal });

    this.querySelector('[data-kc-h15-sticky-submit]')?.addEventListener('click', (event) => {
      event.preventDefault();
      if (!this.mainSubmit || this.mainSubmit.disabled) return;
      this.mainSubmit.click();
    }, { signal });

    document.addEventListener('toggle', () => requestAnimationFrame(() => this.#updateStickyVisibility()), {
      capture: true,
      signal,
    });

    window.addEventListener('resize', () => this.#updateStickyVisibility(), { passive: true, signal });
  }

  #setupObservers() {
    const observedBuyTarget = this.mainSubmit || this.buyButtons || this.productForm;
    if (observedBuyTarget) {
      this.#buyObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const rect = entry.boundingClientRect;
        this.#hasPassedBuyButtons = !entry.isIntersecting && rect.bottom < 0;
        this.#updateStickyVisibility();
      }, { threshold: 0.01 });
      this.#buyObserver.observe(observedBuyTarget);
    }

    const footer = document.querySelector('footer, .footer, [class*="footer-group"]');
    if (footer) {
      this.#footerObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        this.#nearFooter = entry.isIntersecting;
        this.#updateStickyVisibility();
      }, { rootMargin: '180px 0px 0px 0px' });
      this.#footerObserver.observe(footer);
    }

    if (this.productForm) {
      this.#mutationObserver = new MutationObserver(() => this.#syncVariantState());
      this.#mutationObserver.observe(this.productForm, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['value', 'disabled', 'checked'],
      });
    }
  }

  #label(name, fallback = '') {
    return this.dataset[name] || fallback;
  }

  #replaceCount(template, count) {
    return String(template || '').replace('__COUNT__', String(count));
  }

  #currentVariant() {
    if (!this.idInput && this.productForm) {
      this.idInput = this.productForm.querySelector('input[name="id"]');
    }
    const id = this.idInput?.value ? String(this.idInput.value) : '';
    return this.#variants.find((variant) => String(variant.id) === id) || null;
  }

  #currentQuantity() {
    if (!this.quantityInput && this.productForm) {
      this.quantityInput = this.productForm.querySelector('input[name="quantity"]');
    }
    const quantity = Number.parseInt(this.quantityInput?.value || '1', 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  #syncQuantityState() {
    const quantity = this.#currentQuantity();
    const stickyQuantity = this.querySelector('[data-kc-h15-sticky-quantity]');
    if (!stickyQuantity) return;
    stickyQuantity.textContent = quantity > 1 ? ` (${quantity})` : '';
    stickyQuantity.hidden = quantity <= 1;
  }

  #syncVariantState() {
    if (!this.productForm) return;
    this.idInput = this.productForm.querySelector('input[name="id"]') || this.idInput;
    this.quantityInput = this.productForm.querySelector('input[name="quantity"]') || this.quantityInput;
    this.mainSubmit = this.querySelector('[id^="ProductSubmitButton-"]') || this.mainSubmit;

    const variant = this.#currentVariant();
    const variantUnavailable = !variant || !variant.available;
    const purchaseDisabled = variantUnavailable || Boolean(this.mainSubmit?.disabled);

    const stickyPrice = this.querySelector('[data-kc-h15-sticky-price]');
    const stickyVariant = this.querySelector('[data-kc-h15-sticky-variant]');
    const stickySubmit = this.querySelector('[data-kc-h15-sticky-submit]');
    const stickySubmitLabel = this.querySelector('[data-kc-h15-sticky-submit-label]');
    const stickyImage = this.querySelector('[data-kc-h15-sticky-image]');
    const stickyImageWrap = this.querySelector('[data-kc-h15-sticky-image-wrap]');
    const selectedVariantTitle = this.querySelector('[data-kc-selected-variant-title]');

    if (variant && stickyPrice) stickyPrice.textContent = variant.price_formatted || '';
    if (stickyVariant && variant) {
      stickyVariant.textContent = variant.title || '';
      stickyVariant.hidden = !variant.title || variant.title === 'Default Title';
    }
    if (selectedVariantTitle && variant) selectedVariantTitle.textContent = variant.title || '';

    if (stickyImageWrap) {
      const hasImage = Boolean(variant?.image_url);
      stickyImageWrap.hidden = !hasImage;
      if (stickyImage && hasImage && stickyImage.getAttribute('src') !== variant.image_url) {
        stickyImage.setAttribute('src', variant.image_url);
        stickyImage.removeAttribute('srcset');
      }
    }

    if (stickySubmit) stickySubmit.disabled = purchaseDisabled;
    if (stickySubmitLabel) {
      stickySubmitLabel.textContent = variantUnavailable
        ? this.#label('labelSoldOut', 'Sold out')
        : this.#label('labelAdd', 'Add to cart');
    }

    const saleBadge = this.querySelector('[data-kc-sale-badge]');
    const soldoutBadge = this.querySelector('[data-kc-soldout-badge]');
    const urgency = this.querySelector('[data-kc-urgency-line]');
    const inventory = this.querySelector('[id^="Inventory-"]');

    const isSale = Boolean(variant?.available && variant.previous_price_30d && variant.previous_price_30d > variant.price);
    if (saleBadge) {
      saleBadge.hidden = !isSale;
      saleBadge.textContent = isSale ? (saleBadge.dataset.kcBadgeLabel || '') : '';
    }
    if (soldoutBadge) {
      soldoutBadge.hidden = !variantUnavailable;
      soldoutBadge.textContent = variantUnavailable ? (soldoutBadge.dataset.kcBadgeLabel || '') : '';
    }

    if (variant && urgency) {
      urgency.classList.toggle('kc-urgency-line--stock', Boolean(variant.available));
      if (!variant.available) {
        urgency.textContent = this.#label('labelUnavailable', 'Unavailable');
      } else if (variant.inventory_management === 'shopify' && variant.inventory_quantity > 0 && variant.inventory_quantity <= 5) {
        urgency.textContent = this.#replaceCount(this.#label('labelFewLeft', 'Only __COUNT__ left in stock'), variant.inventory_quantity);
      } else if (variant.inventory_management === 'shopify' && variant.inventory_quantity > 5) {
        urgency.textContent = this.#label('labelReady', this.#label('labelInStock', 'In stock'));
      } else if (variant.inventory_management === 'shopify' && variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue') {
        urgency.textContent = this.#label('labelAvailableOrder', 'Available to order');
      } else {
        urgency.textContent = this.#label('labelAvailableOrder', 'Available to order');
      }
    }

    if (variant && inventory) {
      inventory.classList.remove('visibility-hidden');
      if (!variant.available) {
        inventory.textContent = this.#label('labelUnavailable', 'Unavailable');
      } else if (variant.inventory_management === 'shopify' && variant.inventory_quantity > 0 && variant.inventory_quantity <= 3) {
        inventory.textContent = this.#replaceCount(this.#label('labelLowStock', 'Low stock — only __COUNT__ left'), variant.inventory_quantity);
      } else if (variant.inventory_management === 'shopify' && variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue') {
        inventory.textContent = this.#label('labelAvailableOrder', 'Available to order');
      } else {
        inventory.textContent = this.#label('labelInStock', 'In stock');
      }
    }

    this.#syncQuantityState();
    this.#updateStickyVisibility();
  }

  #hasBlockingOverlay() {
    return Boolean(document.querySelector('theme-drawer dialog[open], dialog[open]'));
  }

  #isChatActive() {
    const chat = document.querySelector('shopify-chat');
    if (!chat) return false;
    const style = getComputedStyle(chat);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  #updateStickyVisibility() {
    if (!this.stickyCart) return;
    const variant = this.#currentVariant();
    const available = Boolean(variant?.available) && !this.mainSubmit?.disabled;
    const shouldShow =
      this.#hasPassedBuyButtons &&
      !this.#nearFooter &&
      available &&
      !this.#hasBlockingOverlay() &&
      !this.#isChatActive();

    if (shouldShow) {
      this.stickyCart.hidden = false;
      requestAnimationFrame(() => this.stickyCart?.classList.add('is-visible'));
    } else {
      this.stickyCart.classList.remove('is-visible');
      window.setTimeout(() => {
        if (!this.stickyCart?.classList.contains('is-visible')) this.stickyCart.hidden = true;
      }, 230);
    }
  }
}

if (!customElements.get('kc-h15-product-shell')) {
  customElements.define('kc-h15-product-shell', KcH15ProductShell);
}